import { Link } from 'react-router-dom';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../api/client';
import { Chargement, ErreurRessource } from '../components/EtatRessource';

import type {
  ContentType,
  CorpusEditionRequest,
  CorpusFiltres,
  CorpusItemDetail,
  CorpusItemResume,
  NiveauScolaire,
  OptionCorpus,
  PorteeRevision,
} from '@silex/shared';
import {
  CONTENT_STATUSES,
  CONTENT_TYPES,
  EXIGENCES_COGNITIVES,
  MATIERES,
  NIVEAUX_AVEC_SEQUENCE_MATHS,
  NIVEAUX_SCOLAIRES,
  SEQUENCES_MATHS,
  STATUTS_RELECTURE,
} from '@silex/shared';

import { ApercuExercice } from '../components/ApercuExercice';
import {
  ChampsFigureFonction,
  ETAT_FIGURE_PAR_DEFAUT,
  figureIntracable,
  specDeLEtat,
  type EtatFigure,
} from '../components/ChampsFigureFonction';
import {
  ChampsGrille,
  etatGrilleDepuis,
  grilleChangee,
  grilleDepuisEtat,
  grilleRefusee,
  type EtatGrille,
} from '../components/ChampsGrille';
import { useAuth } from '../auth/AuthContext';

/**
 * LA BANQUE DE SAVOIRS — lister, ouvrir, corriger (Sprint 24).
 *
 * ─── Ce que cet écran est, et ce qu'il n'est pas ───────────────────────────
 *
 * C'est une FILE DE RELECTURE, pas un catalogue. Le corpus est publié d'abord
 * et relu ensuite (décision du fondateur, 2026-09-03) : les items sont déjà
 * servis à des élèves pendant qu'on les relit. L'écran ouvre donc sur ce qui
 * n'a jamais été lu, et l'API trie dans cet ordre — pas alphabétiquement.
 *
 * ─── Le choix d'interface qui porte la décision produit ────────────────────
 *
 * Le formulaire de correction demande une PORTÉE avant tout le reste, et il
 * explique ce que chacune entraîne. Ce n'est pas une case administrative :
 * c'est la question qui décide si l'histoire des mesures reste vraie.
 *
 *   * « forme » — on corrige l'habillage. L'item garde son identité, sa version
 *     avance, le texte d'avant est archivé, et les mesures déjà écrites restent
 *     lisibles telles qu'elles ont été prises.
 *   * « fond » — ce qui est demandé change. L'item SORT du service au lieu
 *     d'être réécrit, parce qu'un élève qui aurait vu la version corrigée
 *     aurait pu répondre autrement.
 *
 * Le compteur de tentatives est affiché à côté de chaque item pour la même
 * raison : avant de corriger, on veut savoir si des mesures pendent au bout.
 */

const RIEN = '—';

/** Le plafond du serveur (`LIMITE_DEFAUT`) — le client ne le choisit pas, il le sait. */
const TAILLE_PAGE = 25;

/**
 * Le plafond du serveur sur les indices (`editionSchema`) — cinq, pas plus.
 * Le bouton d'ajout disparaît à ce compte plutôt que de laisser saisir un
 * sixième indice que la correction refuserait ensuite.
 */
const MAX_INDICES = 5;

/**
 * L'ÉTAT DE RELECTURE, SUR DES VARIANTES QUI EXISTENT.
 *
 * `badge--genere`, `badge--relu` et `badge--corrige` n'ont jamais eu de règle :
 * les trois états s'affichaient en pastille neutre, sans couleur. Sur une file
 * de plusieurs centaines d'items, c'est le signal le plus utile du tableau, et
 * il était muet.
 *
 * On réemploie les trois tons du reste de la console plutôt que d'en inventer :
 * jamais relu = ce qui attend (attention), relu = fait (acquis, avec sa coche),
 * corrigé = touché par un humain (accent).
 */
const TON_RELECTURE: Readonly<Record<string, string>> = {
  genere: 'badge--a-revoir',
  relu: 'badge--acquis',
  corrige: 'badge--en-cours',
  /* L0 — marqué « à corriger » depuis la file des exercices du manuel. */
  a_corriger: 'badge--a-revoir',
};

/** Les types du contrat, en français. `demonstration` est le plus nombreux. */
const LIBELLES_TYPE: Readonly<Record<ContentType, string>> = {
  qcm: 'Choix multiple',
  reponse_numerique: 'Réponse numérique',
  reponse_courte: 'Réponse courte',
  appariement: 'Appariement',
  texte_a_trous: 'Texte à trous',
  ordonnancement: 'Ordonnancement',
  redaction: 'Rédaction',
  demonstration: 'Démonstration',
  comprehension_lecture: 'Compréhension de lecture',
};

/**
 * UN FILTRE À TROIS ÉTATS — indifférent, oui, non.
 *
 * Une case à cocher n'en porte que deux et confond « je ne filtre pas » avec
 * « je veux ceux qui n'en ont pas ». Le contrat, lui, distingue les trois :
 * `undefined` n'est pas envoyé, `false` l'est.
 */
/* Le statut de SERVICE, en français. « brouillon_IA » ou « retire » ne veulent
   rien dire pour un relecteur, et un écran qui affiche un nom d'énumération
   demande à son lecteur de connaître le schéma. */
const LIBELLES_STATUT: Readonly<Record<(typeof CONTENT_STATUSES)[number], string>> = {
  brouillon_IA: 'Brouillon',
  en_revue: 'En revue',
  valide: 'Validé',
  publie: 'Publié — servi aux élèves',
  rejete: 'Rejeté',
  retire: 'Retiré du service',
};

function FiltreTroisEtats({
  id,
  libelle,
  valeur,
  onValeur,
  oui,
  non,
}: {
  readonly id: string;
  readonly libelle: string;
  readonly valeur: boolean | undefined;
  readonly onValeur: (v: boolean | undefined) => void;
  readonly oui: string;
  readonly non: string;
}) {
  return (
    <span className="field">
      <label htmlFor={id}>{libelle}</label>
      <select
        id={id}
        className="control"
        value={valeur === undefined ? '' : valeur ? 'oui' : 'non'}
        onChange={(e) => onValeur(e.target.value === '' ? undefined : e.target.value === 'oui')}
      >
        <option value="">Indifférent</option>
        <option value="oui">{oui}</option>
        <option value="non">{non}</option>
      </select>
    </span>
  );
}

function Etiquette({ statut }: { statut: CorpusItemResume['statutRelecture'] }) {
  const texte = statut === 'genere' ? 'Jamais relu' : statut === 'relu' ? 'Relu' : 'Corrigé';
  return <span className={`badge ${TON_RELECTURE[statut] ?? ''}`}>{texte}</span>;
}

/**
 * CE QUE LA LIGNE DIT DE L'EXERCICE — le type, puis ce qu'il porte.
 *
 * La grille n'est marquée que PRÉSENTE. Savoir qu'elle manque ne suffit pas :
 * elle n'est exigée que d'une méthode de vérification `grille_*`, et
 * `CorpusItemResume` ne transporte pas la méthode. Le déduire ici afficherait
 * « sans grille » sur des exercices qui n'en demandent aucune — c'est le filtre
 * « corrigés par grille, sans grille », calculé par le serveur, qui répond.
 */
function marquesDeLItem(item: CorpusItemResume): string {
  const marques = [LIBELLES_TYPE[item.type]];
  if (item.aFigure) marques.push('figure');
  if (item.aSupport) marques.push('texte');
  if (item.aGrille) marques.push('grille');
  return marques.join(' · ');
}

/** La compétence et les séquences du savoir. Séquences vides = toutes. */
function cadreDuSavoir(item: CorpusItemResume): string {
  return [item.competenceCode ?? '', item.sequences.join(', ')].filter((x) => x !== '').join(' · ');
}

export function CorpusPage() {
  const [filtres, setFiltres] = useState<CorpusFiltres>({});
  const [liste, setListe] = useState<readonly CorpusItemResume[] | null>(null);
  const [total, setTotal] = useState(0);
  /**
   * LE CURSEUR, BRANCHÉ LE 14 SEPTEMBRE 2026.
   *
   * Le serveur plafonne la liste à 25 items et rend `curseurSuivant` pour la
   * suite. Cet écran l'ignorait et n'offrait aucun bouton : sur 1 511 items,
   * 1 486 étaient HORS D'ATTEINTE, quels que soient les filtres — qui
   * s'appliquent avant le plafond. La file de relecture n'était pas pénible,
   * elle était mécaniquement impossible à parcourir, et c'est ce qui explique
   * qu'aucun item n'ait jamais été relu.
   */
  const [curseur, setCurseur] = useState<string | null>(null);
  const [chargeSuite, setChargeSuite] = useState(false);
  /**
   * LA PAGINATION, LE 15 SEPTEMBRE 2026.
   *
   * Le fondateur : « il n'y a même pas de multipage pour une liste de plus de
   * 600 items, c'est désagréable à parcourir ». C'était vrai : le bouton
   * « charger la suite » EMPILAIT les pages dans un tableau qui s'allongeait
   * sans fin, sans jamais dire où l'on se trouve ni permettre de revenir.
   *
   * Aucune modification du serveur : il rend déjà `total` et un `curseurSuivant`
   * qui est l'identifiant de la dernière ligne servie. Il suffit donc de garder
   * la PILE des curseurs déjà franchis pour offrir un vrai « précédent », et de
   * compter les pages pour dire « 26–50 sur 686 ».
   */
  const [pile, setPile] = useState<readonly (string | null)[]>([null]);
  const [page, setPage] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await api.listerCorpus(filtres);
      setListe(r.items);
      setTotal(r.total);
      setCurseur(r.curseurSuivant);
      /* Tout changement de filtre RAMÈNE à la première page : rester à la
         page 7 d'une liste qui n'en compte plus que 2 n'a aucun sens. */
      setPile([null]);
      setPage(0);
    } catch (e) {
      setListe(null);
      setCurseur(null);
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    }
  }, [filtres]);

  /** Va à une page déjà atteinte, ou à la suivante. La liste est REMPLACÉE. */
  const allerA = useCallback(
    async (index: number, curseurDeLaPage: string | null) => {
      setErreur(null);
      setChargeSuite(true);
      try {
        const r = await api.listerCorpus({
          ...filtres,
          ...(curseurDeLaPage ? { curseur: curseurDeLaPage } : {}),
        });
        setListe(r.items);
        setTotal(r.total);
        setCurseur(r.curseurSuivant);
        setPage(index);
        setPile((avant) => {
          if (index < avant.length) return avant;
          return [...avant, curseurDeLaPage];
        });
      } catch (e) {
        setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
      } finally {
        setChargeSuite(false);
      }
    },
    [filtres],
  );

  useEffect(() => {
    void charger();
  }, [charger]);

  const majFiltre = (cle: keyof CorpusFiltres, valeur: string) =>
    setFiltres((f) => ({ ...f, [cle]: valeur === '' ? undefined : valeur }));

  const majBooleen = (
    cle: 'avecFigure' | 'avecSupport' | 'sansGrille',
    valeur: boolean | undefined,
  ) => setFiltres((f) => ({ ...f, [cle]: valeur }));

  /* La séquence ne vit qu'en 4e et 5e. Changer pour un niveau qui n'en a pas
     RETIRE le filtre : un filtre qu'on ne voit plus et qui filtre encore ment
     sur ce que la liste montre. */
  const majNiveau = (valeur: string) =>
    setFiltres((f) => {
      const niveau = valeur === '' ? undefined : (valeur as NiveauScolaire);
      const sequenceAdmise = niveau === undefined || NIVEAUX_AVEC_SEQUENCE_MATHS.includes(niveau);
      return { ...f, niveau, ...(sequenceAdmise ? {} : { sequence: undefined }) };
    });

  const sequenceOfferte =
    filtres.niveau === undefined || NIVEAUX_AVEC_SEQUENCE_MATHS.includes(filtres.niveau);

  /* LA RECHERCHE, ENFIN À L'ÉCRAN.

     Le serveur la cherchait sur le titre et l'énoncé depuis Φ13-B — et aucun
     champ ne la déclenchait. Une couture réparée d'un seul côté ne sert à
     personne : c'est exactement le défaut que ce dépôt traque. Même
     temporisation que la compétence, pour ne pas partir à chaque caractère. */
  const [rechercheSaisie, setRechercheSaisie] = useState('');
  useEffect(() => {
    const minuteur = setTimeout(() => {
      const q = rechercheSaisie.trim();
      setFiltres((f) =>
        (f.recherche ?? '') === q ? f : { ...f, recherche: q === '' ? undefined : q },
      );
    }, 400);
    return () => clearTimeout(minuteur);
  }, [rechercheSaisie]);

  /* LA COMPÉTENCE SE SAISIT À LA MAIN. Aucune route ne rend la liste des
     compétences, et la bâtir depuis la page affichée donnerait une liste
     partielle — donc fausse — des 687 items. La frappe attend un instant avant
     de filtrer : une requête par caractère n'apporterait rien. */
  const [competenceSaisie, setCompetenceSaisie] = useState('');
  useEffect(() => {
    const minuteur = setTimeout(() => {
      const code = competenceSaisie.trim();
      setFiltres((f) =>
        (f.competenceCode ?? '') === code
          ? f
          : { ...f, competenceCode: code === '' ? undefined : code },
      );
    }, 400);
    return () => clearTimeout(minuteur);
  }, [competenceSaisie]);

  return (
    <div className="content__inner">
      <div className="page-head">
        <h1>Banque de savoirs</h1>
        {/* L'ENTRÉE VERS L'ATELIER. Un écran qu'aucun lien n'atteint n'existe
            pas : c'est le même défaut que le curseur de cette page, construit
            côté serveur et jamais lu par la console. */}
        <Link className="btn btn--primary btn--sm" to="/atelier">
          Concevoir un exercice
        </Link>
        <p className="note-line">
          Les items sont servis aux élèves pendant qu’on les relit. Cette file ouvre sur ce qui n’a
          jamais été lu.
        </p>
      </div>

      <div className="card">
        <div className="filters" role="group" aria-label="Filtrer le corpus">
          <span className="field">
            <label htmlFor="f-recherche">Rechercher</label>
            <input
              id="f-recherche"
              className="control"
              type="search"
              value={rechercheSaisie}
              placeholder="Un mot du titre ou de l’énoncé, ou un identifiant"
              onChange={(e) => setRechercheSaisie(e.target.value)}
            />
          </span>

          <span className="field">
            <label htmlFor="f-statut">Service</label>
            <select
              id="f-statut"
              className="control"
              value={filtres.statut ?? ''}
              onChange={(e) => majFiltre('statut', e.target.value)}
            >
              <option value="">Tous</option>
              {CONTENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {LIBELLES_STATUT[s]}
                </option>
              ))}
            </select>
          </span>

          <span className="field">
            <label htmlFor="f-matiere">Matière</label>
            <select
              id="f-matiere"
              className="control"
              value={filtres.matiere ?? ''}
              onChange={(e) => majFiltre('matiere', e.target.value)}
            >
              <option value="">Toutes</option>
              {MATIERES.map((m) => (
                <option key={m} value={m}>
                  {m === 'mathematiques' ? 'Mathématiques' : 'Français'}
                </option>
              ))}
            </select>
          </span>

          <span className="field">
            <label htmlFor="f-niveau">Niveau</label>
            <select
              id="f-niveau"
              className="control"
              value={filtres.niveau ?? ''}
              onChange={(e) => majNiveau(e.target.value)}
            >
              <option value="">Tous</option>
              {NIVEAUX_SCOLAIRES.map((n) => (
                <option key={n} value={n}>
                  {n.replace('sec', 'Secondaire ')}
                </option>
              ))}
            </select>
          </span>

          <span className="field">
            <label htmlFor="f-relecture">Relecture</label>
            <select
              id="f-relecture"
              className="control"
              value={filtres.statutRelecture ?? ''}
              onChange={(e) => majFiltre('statutRelecture', e.target.value)}
            >
              <option value="">Tous les états</option>
              {STATUTS_RELECTURE.map((s) => (
                <option key={s} value={s}>
                  {s === 'genere' ? 'Jamais relu' : s === 'relu' ? 'Relu' : 'Corrigé'}
                </option>
              ))}
            </select>
          </span>

          <span className="field">
            <label htmlFor="f-exigence">Exigence</label>
            <select
              id="f-exigence"
              className="control"
              value={filtres.exigence ?? ''}
              onChange={(e) => majFiltre('exigence', e.target.value)}
            >
              <option value="">Tous les degrés</option>
              {EXIGENCES_COGNITIVES.map((x) => (
                <option key={x} value={x}>
                  {x === 'accessible'
                    ? 'Accessible'
                    : x === 'intermediaire'
                      ? 'Intermédiaire'
                      : 'Exigeant'}
                </option>
              ))}
            </select>
          </span>

          <span className="field">
            <label htmlFor="f-type">Type</label>
            <select
              id="f-type"
              className="control"
              value={filtres.type ?? ''}
              onChange={(e) => majFiltre('type', e.target.value)}
            >
              <option value="">Tous les types</option>
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {LIBELLES_TYPE[t]}
                </option>
              ))}
            </select>
          </span>

          {/* Hors 4e et 5e, le programme n'est pas scindé : le filtre ne
              choisirait qu'entre des listes vides. */}
          {sequenceOfferte ? (
            <span className="field">
              <label htmlFor="f-sequence">Séquence</label>
              <select
                id="f-sequence"
                className="control"
                value={filtres.sequence ?? ''}
                onChange={(e) => majFiltre('sequence', e.target.value)}
              >
                <option value="">Toutes les séquences</option>
                {SEQUENCES_MATHS.map((sq) => (
                  <option key={sq} value={sq}>
                    {sq}
                  </option>
                ))}
              </select>
            </span>
          ) : null}

          <span className="field">
            <label htmlFor="f-competence">Compétence</label>
            <input
              id="f-competence"
              className="control"
              value={competenceSaisie}
              placeholder="Code de la compétence"
              onChange={(e) => setCompetenceSaisie(e.target.value)}
            />
          </span>

          <FiltreTroisEtats
            id="f-figure"
            libelle="Figure"
            valeur={filtres.avecFigure}
            onValeur={(v) => majBooleen('avecFigure', v)}
            oui="Avec figure"
            non="Sans figure"
          />

          <FiltreTroisEtats
            id="f-support"
            libelle="Texte support"
            valeur={filtres.avecSupport}
            onValeur={(v) => majBooleen('avecSupport', v)}
            oui="Avec texte"
            non="Sans texte"
          />
        </div>

        {/* LA GRILLE MANQUANTE — le filtre qui donne la liste des exercices
            qu'aucun relecteur ne peut corriger. Il est seul sur sa ligne parce
            qu'il ne se compare à aucun autre : les autres trient la file,
            celui-ci nomme ce qui la bloque. */}
        <label className="check" htmlFor="f-sans-grille">
          <input
            id="f-sans-grille"
            type="checkbox"
            checked={filtres.sansGrille === true}
            onChange={(e) => majBooleen('sansGrille', e.target.checked ? true : undefined)}
          />
          <span>Corrigés par grille, sans grille</span>
        </label>
        <p className="note-line">
          Ces exercices refusent toute correction tant qu’une grille n’est pas saisie.
        </p>
      </div>

      {erreur ? <ErreurRessource message={erreur} /> : null}
      {!liste && !erreur ? <Chargement quoi="la banque de savoirs" /> : null}

      {liste ? (
        <>
          <p className="note-line">
            {liste.length > 0
              ? `${page * TAILLE_PAGE + 1}–${page * TAILLE_PAGE + liste.length} sur ${total}`
              : `0 sur ${total}`}{' '}
            pour ces filtres.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Savoir</th>
                  <th>Degré</th>
                  <th>Relecture</th>
                  <th>Servi</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.titre}</strong>
                      <br />
                      <span className="note-line">{item.id}</span>
                      {/* DES MARQUES, PAS DES COLONNES. La table en porte déjà
                          six : une colonne par signal la rendrait illisible là
                          où une ligne de sous-titre suffit à les lire. */}
                      <span className="cell-sub">{marquesDeLItem(item)}</span>
                    </td>
                    <td>
                      {item.savoirEnonce ?? RIEN}
                      {/* La compétence et la séquence appartiennent au savoir :
                          elles se lisent sous lui, pas en colonnes séparées. */}
                      {cadreDuSavoir(item) !== '' ? (
                        <span className="cell-sub" title={item.competenceEnonce ?? undefined}>
                          {cadreDuSavoir(item)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {item.exigence} · {item.difficulteAuteur}/5
                    </td>
                    <td>
                      <Etiquette statut={item.statutRelecture} /> v{item.version}
                    </td>
                    <td>
                      {/* Un COMPTEUR, jamais un taux : il répond à « corriger
                        celui-ci touche-t-il des mesures déjà écrites ? ». */}
                      {item.nbTentatives} fois
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOuvert(item.id)}
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* LES DEUX SENS. Un bouton « charger la suite » qui empile ne permet
              ni de revenir, ni de savoir où l'on est : sur 686 items, c'est
              une liste qu'on parcourt une fois et qu'on n'ose plus quitter. */}
          {total > TAILLE_PAGE ? (
            <div className="filters" style={{ marginTop: 16, display: 'flex', gap: 12 }}>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={chargeSuite || page === 0}
                onClick={() => void allerA(page - 1, pile[page - 1] ?? null)}
              >
                Précédent
              </button>
              <span className="note-line" aria-live="polite">
                Page {page + 1} sur {Math.max(1, Math.ceil(total / TAILLE_PAGE))}
              </span>
              <button
                type="button"
                className="btn btn--primary"
                disabled={chargeSuite || curseur === null}
                onClick={() => void allerA(page + 1, curseur)}
              >
                {chargeSuite ? 'Chargement…' : 'Suivant'}
              </button>
            </div>
          ) : null}
          {liste.length === 0 ? (
            <p className="note-line">Aucun item du corpus ne correspond à ces filtres.</p>
          ) : null}
        </>
      ) : null}

      {ouvert ? (
        <DetailItem
          id={ouvert}
          onFerme={() => setOuvert(null)}
          onChange={() => {
            setOuvert(null);
            /* RESTER SUR SA PAGE — 15 septembre 2026.
               `charger()` ramène à la première page. C'est juste pour un
               changement de filtre, et faux ici : le relecteur qui corrige un
               item de la page 7 était renvoyé à la page 1, et devait re-traverser
               toute la file pour reprendre où il en était. Sur 687 items à
               relire, c'est la différence entre une file parcourable et une
               file qu'on abandonne. On recharge la page COURANTE, par le
               curseur qui l'a produite. */
            void allerA(page, pile[page] ?? null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * SERVIR CET EXERCICE À UN ÉLÈVE, OU À UNE CLASSE.
 *
 * Le fondateur, 15 septembre 2026 : « un prof devrait même pouvoir servir
 * l'exercice à disposition directe d'un élève ou d'un groupe d'élèves en
 * particulier [...] c'est ça l'intention réelle en arrière ».
 *
 * L'exercice servi passe AVANT le plan d'apprentissage, et cesse d'être servi
 * dès que l'élève l'a fait. Servir deux fois de suite ne l'empile pas ; le
 * redonner plus tard reste possible.
 *
 * RÉSERVÉ AU PROFESSEUR. L'écran du corpus est partagé avec l'administrateur,
 * dont le périmètre d'élèves n'existe pas — `/v1/tuteur/*` lui répond 403. Le
 * bloc ne s'affiche donc pas pour lui, plutôt que de lui montrer une action qui
 * finirait en refus.
 *
 * Les listes sont chargées à l'OUVERTURE du bloc, pas au chargement de la
 * page : la plupart des relectures ne servent rien à personne.
 */
function ServirExercice({ contentItemId }: { contentItemId: string }) {
  const { user } = useAuth();
  const [ouvert, setOuvert] = useState(false);
  const [eleves, setEleves] = useState<readonly { eleveId: string; prenom: string }[]>([]);
  const [classes, setClasses] = useState<readonly { id: string; nom: string }[]>([]);
  const [cible, setCible] = useState('');
  const [consigne, setConsigne] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  if (user?.role !== 'tuteur') return null;

  const ouvrir = async () => {
    setOuvert(true);
    setErreur(null);
    try {
      const [p, c] = await Promise.all([api.getElevesDuTuteur(), api.listerClasses()]);
      setEleves(p.eleves.map((e) => ({ eleveId: e.eleveId, prenom: e.prenom })));
      setClasses(c.classes.map((k) => ({ id: k.id, nom: k.nom })));
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    }
  };

  const servir = async () => {
    if (cible === '') return;
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      const [genre, identifiant] = cible.split(':');
      if (genre === 'eleve') {
        const r = await api.servirExerciceAEleve(
          identifiant!,
          contentItemId,
          consigne || undefined,
        );
        const prenom = eleves.find((e) => e.eleveId === identifiant)?.prenom ?? 'L’élève';
        setMessage(
          r.deja
            ? `${prenom} a déjà cet exercice en attente.`
            : `Servi à ${prenom}. Il passera avant son plan.`,
        );
      } else {
        const r = await api.servirExerciceAClasse(
          identifiant!,
          contentItemId,
          consigne || undefined,
        );
        setMessage(
          r.nbServis === 0
            ? 'Tous les élèves de cette classe l’avaient déjà en attente.'
            : `Servi à ${r.nbServis} élève${r.nbServis > 1 ? 's' : ''}${
                r.nbDeja > 0 ? ` (${r.nbDeja} l’avaient déjà)` : ''
              }.`,
        );
      }
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnCours(false);
    }
  };

  if (!ouvert) {
    return (
      <div className="filters" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn--ghost" onClick={() => void ouvrir()}>
          Servir cet exercice à…
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h3>Servir cet exercice</h3>
      <p className="card-sub">
        Il passera avant le plan d’apprentissage, et cessera d’être servi dès qu’il sera fait.
      </p>

      {erreur ? (
        <p className="callout callout--rare" role="alert">
          {erreur}
        </p>
      ) : null}
      {message ? (
        <p className="callout callout--ok" role="status">
          {message}
        </p>
      ) : null}

      <div className="filters">
        <label className="field field--inline">
          <span>À qui</span>
          <select className="control" value={cible} onChange={(e) => setCible(e.target.value)}>
            <option value="">Choisir…</option>
            {eleves.length > 0 ? (
              <optgroup label="Un élève">
                {eleves.map((e) => (
                  <option key={e.eleveId} value={`eleve:${e.eleveId}`}>
                    {e.prenom}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {classes.length > 0 ? (
              <optgroup label="Une classe">
                {classes.map((k) => (
                  <option key={k.id} value={`classe:${k.id}`}>
                    {k.nom}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        <label className="field field--inline">
          <span>Un mot pour l’élève (optionnel)</span>
          <input
            className="control"
            value={consigne}
            placeholder="Ex. : refais celui-ci avant jeudi."
            onChange={(e) => setConsigne(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn--primary"
          disabled={enCours || cible === ''}
          onClick={() => void servir()}
        >
          {enCours ? 'Envoi…' : 'Servir'}
        </button>
      </div>

      {eleves.length === 0 && classes.length === 0 ? (
        <p className="card-sub">
          Vous ne suivez encore aucun élève. Demandez son code de suivi à un élève depuis{' '}
          <Link to="/eleves">Mes élèves</Link>.
        </p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Le détail et la correction                                                 */
/* -------------------------------------------------------------------------- */

function DetailItem({
  id,
  onFerme,
  onChange,
}: {
  id: string;
  onFerme: () => void;
  onChange: () => void;
}) {
  const [item, setItem] = useState<CorpusItemDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const [portee, setPortee] = useState<PorteeRevision>('forme');
  const [motif, setMotif] = useState('');
  const [titre, setTitre] = useState('');
  const [enonce, setEnonce] = useState('');
  const [consigne, setConsigne] = useState('');
  const [explication, setExplication] = useState('');
  const [options, setOptions] = useState<OptionCorpus[]>([]);
  const [indices, setIndices] = useState<string[]>([]);
  /* LE TITRE, LA CONSIGNE, LES INDICES ET LA GRILLE. Le contrat les acceptait
     déjà et la route les écrit ; seuls les champs manquaient. Le formulaire
     n'envoyait que l'énoncé, l'explication, les options et la figure. */
  const [grille, setGrille] = useState<EtatGrille>(() => etatGrilleDepuis(null));

  /* LA FIGURE. Ce formulaire ne sait produire qu'une COURBE DE FONCTION : une
     figure existante d'une autre famille — triangle, cercle, solide — ne peut
     pas être relue dans ces champs. On ne fait donc pas semblant : on l'affiche
     telle qu'elle est, et on permet de la RETIRER ou de la REMPLACER par une
     fonction. Prétendre l'éditer coefficient par coefficient produirait des
     figures fausses. */
  const [avecFigure, setAvecFigure] = useState(false);
  const [figure, setFigure] = useState<EtatFigure>(ETAT_FIGURE_PAR_DEFAUT);
  const [retirerFigure, setRetirerFigure] = useState(false);

  /* La suppression se confirme : on ne détruit pas un exercice sur un tap. */
  const [confirmeSuppression, setConfirmeSuppression] = useState(false);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const d = await api.ouvrirItemCorpus(id);
        if (!vivant) return;
        setItem(d);
        setTitre(d.titre);
        setEnonce(d.enonce);
        setConsigne(d.consigne);
        setExplication(d.explication ?? '');
        setOptions([...d.options]);
        setIndices([...d.indices]);
        setGrille(etatGrilleDepuis(d.grille));
      } catch (e) {
        if (vivant) setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
      }
    })();
    return () => {
      vivant = false;
    };
  }, [id]);

  /* CE QUE LE RELECTEUR PEUT POINTER — 15 septembre 2026.

     Cette liste se remplissait des erreurs DÉJÀ pointées par l'item. Pour un
     exercice généré, qui n'en pointe aucune, elle était donc VIDE — alors que
     le validateur exige que chaque leurre nomme la croyance qu'il matérialise.
     Le relecteur était sommé de faire quelque chose que l'écran ne lui offrait
     pas, et sa correction était refusée sans issue.

     Le serveur rend désormais les erreurs types autorisées pour le savoir de
     l'item, calculées par la MÊME règle que le validateur : l'écran ne peut
     plus proposer un code qui sera rejeté à l'enregistrement. */
  const erreursOffertes = useMemo(() => item?.erreursDisponibles ?? [], [item]);

  /* La grille ne s'offre que là où elle sert : un item corrigé par grille, ou
     qui en porte déjà une. Partout ailleurs, elle inviterait à remplir un
     barème qu'aucun correcteur ne lirait. */
  const grillePertinente =
    item !== null &&
    (item.grille !== null ||
      item.methodeVerification === 'grille_modele' ||
      item.methodeVerification === 'grille_humaine');

  /* Le cas qui bloque 247 démonstrations : corrigé par grille, sans grille. */
  const grilleManquante =
    item !== null &&
    item.grille === null &&
    (item.methodeVerification === 'grille_modele' || item.methodeVerification === 'grille_humaine');

  const grilleAEnvoyer = grillePertinente && grilleChangee(grille, item?.grille ?? null);
  const refusGrille = grilleAEnvoyer ? grilleRefusee(grille) : null;

  /* Un indice vide ne part pas : le schéma refuse la chaîne vide, et une ligne
     laissée en blanc ferait refuser toute la correction. */
  const indicesEnvoyes = indices.map((s) => s.trim()).filter((s) => s.length > 0);
  const indicesChanges =
    JSON.stringify(indicesEnvoyes) !== JSON.stringify([...(item?.indices ?? [])]);

  const enregistrer = async () => {
    setErreur(null);

    /* LES REFUS LOCAUX, AVANT L'ALLER-RETOUR. Le serveur refuse les mêmes
       grilles, mais son message nomme le CODE DE L'ITEM, pas le champ fautif :
       illisible pour qui vient de remplir un formulaire. */
    if (portee === 'forme' && titre.trim() === '') {
      setErreur('Le titre ne peut pas être vide.');
      return;
    }
    if (portee === 'forme' && refusGrille) {
      setErreur(refusGrille);
      return;
    }
    if (portee === 'forme' && avecFigure && !retirerFigure && figureIntracable(true, figure)) {
      setErreur(
        'Cette figure ne se trace pas avec ces valeurs. Corrigez le domaine ou les coefficients — elle ne serait jamais servie à l’élève.',
      );
      return;
    }

    setEnCours(true);
    try {
      const correction: CorpusEditionRequest = {
        portee,
        motif,
        ...(portee === 'forme'
          ? {
              titre: titre.trim(),
              enonce,
              /* Une consigne vide ne part pas : le schéma exige au moins un
                 caractère, et la base en porte d'absentes. */
              ...(consigne.trim() === '' ? {} : { consigne: consigne.trim() }),
              explication: explication || undefined,
              options: options.length > 0 ? options : undefined,
              ...(indicesChanges ? { indices: indicesEnvoyes } : {}),
              /* La version s'incrémente ICI et jamais à la main : elle entre
                 dans la clé du cache de verdicts. Renvoyer un barème identique
                 la ferait avancer pour rien, d'où l'envoi sous condition. */
              ...(grilleAEnvoyer
                ? { grille: grilleDepuisEtat(grille, item?.grille?.version ?? 0) }
                : {}),
              /* `null` retire la figure ; une spécification la remplace ;
                 l'absence la laisse intacte. */
              ...(retirerFigure
                ? { figure: null }
                : avecFigure
                  ? {
                      figure: specDeLEtat(true, figure) as unknown as Record<string, unknown>,
                    }
                  : {}),
            }
          : {}),
      };
      await api.corrigerItemCorpus(id, correction);
      onChange();
    } catch (e) {
      // Un refus de validation porte les PHRASES du validateur : « une option
      // ne pointe aucune erreur type », « l'énoncé parle d'un pourcentage de
      // maîtrise ». On les affiche telles quelles — elles disent quoi corriger.
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnCours(false);
    }
  };

  /**
   * SUPPRIMER — et dire quoi faire quand c'est impossible.
   *
   * La route refuse (409) dès qu'une mesure d'élève retient l'exercice : des
   * tentatives y renvoient, et les effacer laisserait ces traces dans le vide.
   * Le message du serveur nomme la sortie — retirer l'item du service — et on
   * l'affiche tel quel, parce qu'il dit exactement quoi faire.
   */
  const supprimer = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      await api.supprimerItemCorpus(id);
      onChange();
      onFerme();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
      setConfirmeSuppression(false);
    } finally {
      setEnCours(false);
    }
  };

  const marquerRelu = async () => {
    setEnCours(true);
    try {
      await api.marquerItemRelu(id);
      onChange();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setEnCours(false);
    }
  };

  if (!item && !erreur) return <Chargement quoi="l’item" />;

  return (
    <div className="card card--calm">
      <div className="page-head">
        <h2>{item?.titre ?? id}</h2>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onFerme}>
          Fermer
        </button>
      </div>

      {erreur ? <ErreurRessource message={erreur} /> : null}

      {item ? (
        <>
          <p className="note-line">
            {item.savoirCode} · {item.notionTitre} · {item.exigence} · version {item.version} ·
            servi {item.nbTentatives} fois
          </p>

          {/* LE MUR DE LA GRILLE, DIT EN HAUT DU PANNEAU. `validerItem` refuse
              toute correction d'un item corrigé par grille qui n'en porte
              aucune, et `methodeVerification` ne s'édite pas. Sans cette
              ligne, le relecteur ne découvrait l'impasse qu'après avoir tout
              ressaisi. */}
          {grilleManquante ? (
            <p className="callout callout--rare" role="status">
              Cet exercice se corrige par grille et n’en porte aucune. Aucune correction ne sera
              acceptée tant qu’une grille n’est pas saisie.
            </p>
          ) : null}

          {/* L'ACTION DE PREMIER RANG. Sur une file de 687 items, la plupart
              n'ont rien à corriger : « relu » est le geste courant de cet
              écran, pas le second bouton d'une rangée de fin de formulaire. */}
          <div className="filters">
            <button type="button" className="btn" disabled={enCours} onClick={marquerRelu}>
              J’ai lu, rien à changer
            </button>
          </div>
          <p className="note-line">
            « J’ai lu, rien à changer » promeut l’item dans la banque : sa provenance affichée à
            l’élève passe de « généré pour toi » à « validé par un pédagogue ».
          </p>

          {/* DEUX COLONNES — l'exercice servi à gauche, les champs à droite.
              Une longue page où l'on descend fait corriger de mémoire : le
              relecteur ne voyait plus l'énoncé au moment de juger l'option.
              `.tuteur-grid` est la grille à deux colonnes de la console ; elle
              s'empile d'elle-même sous 1000 px, l'aperçu en premier. */}
          <div className="tuteur-grid">
            {/* La cellule s'étire pour que l'aperçu tienne pendant qu'on
                descend les champs : la grille aligne sinon ses cellules en
                haut, et une cellule à la hauteur de son contenu ne laisse
                aucune course à `sticky`. */}
            <div style={{ alignSelf: 'stretch' }}>
              <div style={{ position: 'sticky', top: 16 }}>
                {/* Sous « fond », aucun contenu n'est écrit : l'aperçu montre
                    alors l'item tel qu'il est servi, jamais une saisie qui ne
                    partira pas. */}
                <ApercuExercice
                  titre={portee === 'forme' ? titre : item.titre}
                  enonce={portee === 'forme' ? enonce : item.enonce}
                  consigne={portee === 'forme' ? consigne : item.consigne}
                  options={portee === 'forme' ? options : item.options}
                  figure={portee === 'forme' && retirerFigure ? null : item.figure}
                  /* Le support ne s'édite pas : `CorpusEditionRequest` n'en
                     porte aucun champ. Il ne suit donc pas la saisie. */
                  support={item.support}
                />
              </div>
            </div>

            <div>
              {/* LA PORTÉE AVANT LE CONTENU. « fond » retire l'item du service
                  sans rien réécrire : laisser les champs de contenu sous ce
                  choix faisait saisir un texte perdu en silence à l'envoi. */}
              <fieldset>
                <legend>Portée de la correction</legend>
                <label>
                  <input
                    type="radio"
                    name="portee"
                    checked={portee === 'forme'}
                    onChange={() => setPortee('forme')}
                  />{' '}
                  <strong>Forme</strong> — coquille, tournure, ponctuation. Ce qui est demandé et la
                  réponse attendue ne changent pas. L’item garde son identité, sa version avance, et
                  le texte d’avant reste lisible.
                </label>
                <label>
                  <input
                    type="radio"
                    name="portee"
                    checked={portee === 'fond'}
                    onChange={() => setPortee('fond')}
                  />{' '}
                  <strong>Fond</strong> — les données, la question ou la réponse attendue changent.
                  Ce n’est plus le même item : il SORT du service et reste lisible. Les{' '}
                  {item.nbTentatives} mesures déjà prises continuent de pointer vers le texte qui
                  les a produites.
                </label>
              </fieldset>

              {portee === 'forme' ? (
                <>
                  <span className="field field--bloc">
                    <label htmlFor="d-titre">Titre</label>
                    <input
                      id="d-titre"
                      className="control"
                      value={titre}
                      onChange={(e) => setTitre(e.target.value)}
                    />
                  </span>

                  <span className="field field--bloc">
                    <label htmlFor="d-enonce">Énoncé</label>
                    <textarea
                      id="d-enonce"
                      className="control"
                      rows={4}
                      value={enonce}
                      onChange={(e) => setEnonce(e.target.value)}
                    />
                  </span>

                  <span className="field field--bloc">
                    <label htmlFor="d-consigne">Consigne</label>
                    <textarea
                      id="d-consigne"
                      className="control"
                      rows={2}
                      value={consigne}
                      onChange={(e) => setConsigne(e.target.value)}
                    />
                  </span>

                  {options.length > 0 ? (
                    <fieldset>
                      <legend>
                        Options — chaque mauvaise réponse doit pointer une erreur type. Un leurre
                        qui ne dit rien n’apprend rien, ni à l’élève, ni au tuteur, ni au produit.
                      </legend>
                      {options.map((o, i) => (
                        <div key={o.id} className="filters">
                          <span className="badge">{o.id}</span>
                          <input
                            className="control"
                            value={o.texte}
                            aria-label={`Texte de l’option ${o.id}`}
                            onChange={(e) =>
                              setOptions((liste) =>
                                liste.map((x, j) =>
                                  i === j ? { ...x, texte: e.target.value } : x,
                                ),
                              )
                            }
                          />
                          <select
                            className="control"
                            value={o.erreurType ?? ''}
                            aria-label={`Erreur type de l’option ${o.id}`}
                            onChange={(e) =>
                              setOptions((liste) =>
                                liste.map((x, j) =>
                                  i === j
                                    ? e.target.value === ''
                                      ? { id: x.id, texte: x.texte }
                                      : { ...x, erreurType: e.target.value }
                                    : x,
                                ),
                              )
                            }
                          >
                            <option value="">Bonne réponse</option>
                            {erreursOffertes.map((e) => (
                              <option key={e.code} value={e.code}>
                                {e.libelle}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </fieldset>
                  ) : null}

                  <span className="field field--bloc">
                    <label htmlFor="d-explication">Explication rendue après le verdict</label>
                    <textarea
                      id="d-explication"
                      className="control"
                      rows={3}
                      value={explication}
                      onChange={(e) => setExplication(e.target.value)}
                    />
                  </span>

                  <div className="card">
                    <h3>Indices, du plus léger au plus explicite</h3>
                    {indices.map((indice, i) => (
                      <div key={i} className="filters">
                        <span className="badge">{i + 1}</span>
                        <input
                          className="control"
                          value={indice}
                          aria-label={`Indice ${i + 1}`}
                          onChange={(e) =>
                            setIndices((liste) =>
                              liste.map((x, j) => (i === j ? e.target.value : x)),
                            )
                          }
                        />
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setIndices((liste) => liste.filter((_, j) => i !== j))}
                        >
                          Retirer
                        </button>
                      </div>
                    ))}
                    {indices.length < MAX_INDICES ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setIndices((liste) => [...liste, ''])}
                      >
                        Ajouter un indice
                      </button>
                    ) : null}
                    <p className="note-line">Aucun indice ne donne le résultat.</p>
                  </div>

                  {grillePertinente ? (
                    <ChampsGrille etat={grille} onEtat={setGrille} refus={refusGrille} />
                  ) : null}

                  {item.figure && !retirerFigure ? (
                    <div className="card">
                      <h3>La figure actuelle</h3>
                      {/* Le tracé est passé à la colonne de gauche, avec le
                          reste de ce que l'élève reçoit. Le montrer deux fois
                          ferait douter qu'il s'agisse de la même figure. */}
                      <label className="field field--inline">
                        <input
                          type="checkbox"
                          checked={retirerFigure}
                          onChange={(e) => setRetirerFigure(e.target.checked)}
                        />
                        <span>Retirer cette figure de l’exercice</span>
                      </label>
                    </div>
                  ) : null}

                  {retirerFigure ? (
                    <p className="callout callout--calm">
                      La figure sera retirée. L’énoncé, lui, ne change pas.{' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setRetirerFigure(false)}
                      >
                        Annuler
                      </button>
                    </p>
                  ) : (
                    <ChampsFigureFonction
                      actif={avecFigure}
                      onActif={setAvecFigure}
                      etat={figure}
                      onEtat={setFigure}
                      libelleActivation={
                        item.figure
                          ? 'Remplacer la figure par la représentation d’une fonction'
                          : 'Joindre la représentation graphique d’une fonction'
                      }
                    />
                  )}
                </>
              ) : (
                <p className="note-line">
                  La portée « fond » retire l’item du service. Aucun contenu n’est écrit.
                </p>
              )}

              <span className="field field--bloc">
                <label htmlFor="d-motif">Ce que vous corrigez, et pourquoi</label>
                <textarea
                  id="d-motif"
                  className="control"
                  rows={2}
                  value={motif}
                  placeholder="Une phrase. C’est elle qu’on relira dans six mois."
                  onChange={(e) => setMotif(e.target.value)}
                />
              </span>

              {item.revisions.length > 0 ? (
                <div className="card">
                  <h3>Corrections passées</h3>
                  <ul>
                    {item.revisions.map((r) => (
                      <li key={r.id}>
                        v{r.versionAvant} → v{r.versionApres} · {r.portee} · {r.auteur} ·{' '}
                        {new Date(r.creeLe).toLocaleDateString('fr-CA')} — {r.motif}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <ServirExercice contentItemId={id} />

              <div className="filters">
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={enCours || motif.trim().length < 20}
                  onClick={enregistrer}
                >
                  Enregistrer la correction
                </button>
                {confirmeSuppression ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={enCours}
                      onClick={() => setConfirmeSuppression(false)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={enCours}
                      onClick={supprimer}
                    >
                      Confirmer la suppression
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={enCours}
                    onClick={() => setConfirmeSuppression(true)}
                  >
                    Supprimer l’exercice
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
